/**
 * Uses storyStore for artist story (api/v2/artist/story/).
 * Returns sanitized HTML for rich display and plain text for summaries.
 */

import { useEffect, useMemo } from "react";
import { useStoryStore } from "@/stores/storyStore";
import { STORY_PLACEHOLDER } from "@/constants/artistStory";
import { htmlToPlainText, sanitizeStoryHtml } from "@/utils/sanitizeStoryHtml";

export { STORY_PLACEHOLDER };

export function useArtistStory() {
  const data = useStoryStore((s) => s.data);
  const ensureFetched = useStoryStore((s) => s.ensureFetched);
  const isLoading = useStoryStore((s) => s.isLoading);
  const fetchStory = useStoryStore((s) => s.fetchStory);

  useEffect(() => {
    ensureFetched();
  }, [ensureFetched]);

  const raw = data?.story;
  const hasStory = typeof raw === "string" && raw.trim().length > 0;

  const storyHtml = useMemo(() => {
    if (!hasStory || !raw) return null;
    const sanitized = sanitizeStoryHtml(raw.trim());
    return sanitized.length > 0 ? sanitized : null;
  }, [hasStory, raw]);

  const storyText = useMemo(() => {
    if (!hasStory || !raw) return STORY_PLACEHOLDER;
    return htmlToPlainText(raw);
  }, [hasStory, raw]);

  return {
    storyHtml,
    storyText,
    hasStory,
    isLoading,
    isError: false,
    refetch: fetchStory,
  };
}
